<template>
  <div class="root _app-shell">
    <CookieConsent v-if="isOverhaulEnabled" />
    <CookiesNotice v-else />
    <NotifyWrapper />

    <Navigation v-if="navbarControls.isNavbarEnabled" />
    <BannerSurvey v-if="surveyBannerVisibility" />

    <RouterView />
    <Popover>
      <RouterView name="right" />
    </Popover>

    <BackgroundImg />
    <AppFooter />
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";

import AppFooter from "@Components/main/AppFooter.vue";
import BackgroundImg from "@Components/main/BackgroundImg.vue";
import CookiesNotice from "@Components/main/CookiesNotice.vue";
import HlpLazyLoadComponent from "@Helpers/functions/lazyLoadComponent";
import Navigation from "@Components/main/Navigation.vue";
import CookieConsent from "@Components/overhauling/consent/CookieConsent.vue";
import NotifyWrapper from "@Components/notify/NotifyWrapper.vue";

import Popover from "@Popover/Index.vue";
import { useNavbarStore } from "@Resources/js/modules/shared/useNavbarStore";

import useSurvey from "@Composables/useSurvey";

export default {
  name: "App",
  metaInfo() {
    return {
      title: `URL Shortener, Branded Short Links & Analytics | TinyURL`,
      meta: [
        {
          vmid: `description`,
          name: `description`,
          content: `Create short and branded links with TinyURL. Track link performance, import or purchase branded domains, and run campaigns with bulk link shortening.`,
        },
        {
          vmid: `keywords`,
          name: `keywords`,
          content: `URL shortener, create short links, branded domains, bulk link shortening`,
        },
      ],
      link: [
        {
          vmid: `canonical`,
          rel: `canonical`,
          href: window.location.origin + this.$route.path,
        },
      ],
    };
  },

  components: {
    BackgroundImg,
    CookiesNotice,
    AppFooter,
    Navigation,
    Popover,
    CookieConsent,
    NotifyWrapper,
    BannerSurvey: HlpLazyLoadComponent("banners/BannerSurvey"),
  },

  setup() {
    const { fetchSurvey } = useSurvey();

    return {
      fetchSurvey,
      navbarControls: useNavbarStore(),
    };
  },

  computed: {
    ...mapGetters([
      "isSurveyEnabled",
      "isUserSubscribed",
      "isUserLoggedIn",
      "isOverhaulEnabled",
      "userVerified",
      "user",
    ]),

    surveyBannerVisibility() {
      return (
        this.isSurveyEnabled && this.userVerified && this.user.isSurveyEnabled
      );
    },
  },

  watch: {
    user() {
      this.loadSurvey();
    },
  },

  created() {
    this.loadSurvey();
  },

  mounted() {
    window.addEventListener("resize", this.handleResize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },

  methods: {
    ...mapMutations(["UPDATE_USER_AGENT"]),

    loadSurvey() {
      const isSurveyAllowed = (() => {
        const currentPage = this.$route.name;
        const forbiddenPages = new Set([
          "settings.subscription.success",
          "teams.team.subscription.success",
          "org.settings.profile.subscription.success",
          "freetrial.domain-purchase-verification-modal",
        ]);
        const isSubscribing =
          "subscription" in this.$route.query &&
          "interval" in this.$route.query;

        return !forbiddenPages.has(currentPage) && !isSubscribing;
      })();

      if (!isSurveyAllowed) return;

      const section = "onboarding";
      const url = this.$apiUrl(`survey/${section}`);
      this.fetchSurvey(url, section);
    },

    handleResize() {
      this.UPDATE_USER_AGENT();
    },
  },
};
</script>
